import {
    ButtonGradientPrimary,
    ButtonSecondary,
    Colors,
    FileDropZone,
    Icon,
    ModalDialog,
    Text,
} from '@amelio/ui-library';
import { useState } from 'react';

import { COMPENSATION_CAPTION } from '../compensationStyles';
import { useReportFailure } from '../hooks/useReportFailure';
import { buildSalaryTemplate } from '../utils/salaryTemplate';

import type { TranslationKey } from '@/i18n-key';
import type {
    CompensationLine,
    CompensationRecognizedSalary,
    CompensationSalaryImportIssue,
    CompensationSalaryParseResult,
} from '@/types/compensation';

import { useParseCompensationSalaries } from '@/api/compensation';
import { useAmelioTranslations, useSelectedAppLanguage } from '@/hooks';
import { downloadFile } from '@/utils/fileDownload';
import { formatCurrency } from '@/utils/numberFormat';

/** Why one row was rejected: the backend sends a stable issue name, never a sentence. */
const ISSUE_KEYS: Record<CompensationSalaryImportIssue, TranslationKey> = {
    MissingAmount: 'compensation.import.reasons.missingAmount',
    InvalidAmount: 'compensation.import.reasons.invalidAmount',
    AmountOutOfRange: 'compensation.import.reasons.amountOutOfRange',
    MissingName: 'compensation.import.reasons.missingName',
    AmbiguousName: 'compensation.import.reasons.ambiguousName',
    AmbiguousAmount: 'compensation.import.reasons.ambiguousAmount',
};

/** How many names of a review list are spelled out before the rest is left implicit. */
const REVIEW_PREVIEW_LIMIT = 5;

/** How many recognized rows are shown with the salary they will write. Confirming this modal
 * overwrites every matched person's current salary at once, so the reader has to see the shape of
 * what is about to be written, not only how many rows it covers — four fit on one line at the
 * modal's width, and `compensation.import.matchedMore` accounts for the rest. */
const MATCHED_PREVIEW_LIMIT = 4;

const TEXTAREA_ID = 'compensation-import-content';

interface ImportSalariesModalProps {
    isOpen: boolean;
    onClose: () => void;
    envelopeId: string;
    /** The envelope's computed lines: what the downloadable template is built from. */
    lines: CompensationLine[];
    onApply: (salaries: CompensationRecognizedSalary[]) => void;
}

/**
 * Bulk entry of the current salaries the report has no other way of knowing. The file is parsed
 * and matched by the backend — nothing is written by that call — and the recognized rows only
 * reach the draft once the reader has seen what was and was not understood, and confirmed.
 */
export const ImportSalariesModal = ({
    isOpen,
    onClose,
    envelopeId,
    lines,
    onApply,
}: ImportSalariesModalProps) => {
    const { t } = useAmelioTranslations();
    const reportFailure = useReportFailure();
    // Read here rather than taken as a prop: the amounts previewed below are the only thing in this
    // modal that is formatted per language, and the page reads the same value from the same hook.
    const language = useSelectedAppLanguage();

    const [content, setContent] = useState('');
    const [result, setResult] = useState<CompensationSalaryParseResult | null>(null);

    const { mutateAsync: parseSalaries, isPending: isParsing } = useParseCompensationSalaries();

    const closeAndReset = () => {
        setContent('');
        setResult(null);
        onClose();
    };

    const analyze = async (rawContent: string) => {
        if (rawContent.trim() === '') {
            setResult(null);
            return;
        }

        try {
            setResult(await parseSalaries({ id: envelopeId, content: rawContent }));
        } catch (error) {
            setResult(null);
            reportFailure(error, 'compensation.import.error');
        }
    };

    const handleFileSelect = async (file: File) => {
        const fileContent = await file.text();

        setContent(fileContent);
        await analyze(fileContent);
    };

    const handleDownloadTemplate = () => {
        const blob = new Blob([buildSalaryTemplate(lines)], { type: 'text/csv;charset=utf-8' });
        downloadFile(blob, `${t('compensation.import.templateFileName')}.csv`);
    };

    const handleConfirm = () => {
        if (!result) {
            return;
        }

        onApply(result.recognized);
        closeAndReset();
    };

    const recognized = result?.recognized ?? [];
    const recognizedCount = recognized.length;

    const matchedPreview = recognized
        .slice(0, MATCHED_PREVIEW_LIMIT)
        .map((row) => `${row.displayName} · ${formatCurrency(row.amount, language)}`)
        .join(' — ');
    const matchedOverflowCount = recognizedCount - MATCHED_PREVIEW_LIMIT;

    return (
        <ModalDialog
            isOpen={isOpen}
            onClose={closeAndReset}
            closeAriaLabel={t('common.close')}
            title={t('compensation.import.title')}
            subtitle={t('compensation.import.description')}
            size={'md'}
            dataTestId={'compensation-import-modal'}
            footer={
                <div className={'flex justify-end gap-2'}>
                    <ButtonSecondary
                        type={'button'}
                        size={'sm'}
                        onClick={closeAndReset}
                        dataTestId={'compensation-import-cancel'}
                    >
                        {t('compensation.import.cancel')}
                    </ButtonSecondary>

                    {result === null ? (
                        <ButtonGradientPrimary
                            type={'button'}
                            size={'sm'}
                            onClick={() => void analyze(content)}
                            loading={isParsing}
                            disabled={isParsing || content.trim() === ''}
                            dataTestId={'compensation-import-analyze'}
                        >
                            {t('compensation.import.analyze')}
                        </ButtonGradientPrimary>
                    ) : (
                        <ButtonGradientPrimary
                            type={'button'}
                            size={'sm'}
                            onClick={handleConfirm}
                            disabled={recognizedCount === 0}
                            dataTestId={'compensation-import-confirm'}
                        >
                            {t('compensation.import.confirm')}
                        </ButtonGradientPrimary>
                    )}
                </div>
            }
        >
            <div className={'flex flex-col gap-[16px]'}>
                <div className={'flex flex-wrap items-center gap-3'}>
                    <FileDropZone
                        className={'flex-1'}
                        ariaLabel={t('compensation.import.chooseFile')}
                        promptLabel={t('compensation.import.dropPrompt')}
                        browseLabel={t('compensation.import.dropBrowse')}
                        dragOverLabel={t('compensation.import.dropHere')}
                        uploadingLabel={t('compensation.import.analyzing')}
                        retryLabel={t('compensation.import.dropRetry')}
                        fileInvalidTypeMessage={t('compensation.import.invalidFileType')}
                        fileRejectedLiveMessage={(rejection) =>
                            t('compensation.import.fileRejected', { name: rejection.file.name })
                        }
                        fileSelectedLiveMessage={(files) =>
                            t('compensation.import.fileSelected', {
                                name: files.map((selected) => selected.name).join(', '),
                            })
                        }
                        accept={'.csv,text/csv,text/plain'}
                        file={null}
                        isUploading={isParsing}
                        onSelect={([selected]) => void handleFileSelect(selected)}
                        dataTestId={'compensation-import-file'}
                    />

                    <ButtonSecondary
                        type={'button'}
                        size={'sm'}
                        onClick={handleDownloadTemplate}
                        leftIcon={
                            <Icon
                                name={'arrow-export'}
                                size={'sm'}
                                ariaHidden
                            />
                        }
                        dataTestId={'compensation-import-template'}
                    >
                        {t('compensation.import.downloadTemplate')}
                    </ButtonSecondary>
                </div>

                <div className={'flex flex-col gap-1'}>
                    <label htmlFor={TEXTAREA_ID}>
                        <Text
                            color={Colors.BASE_500}
                            className={COMPENSATION_CAPTION}
                        >
                            {t('compensation.import.templateColumns')}
                        </Text>
                    </label>

                    <textarea
                        id={TEXTAREA_ID}
                        value={content}
                        onChange={(event) => {
                            setContent(event.target.value);
                            setResult(null);
                        }}
                        placeholder={t('compensation.import.pastePlaceholder')}
                        className={
                            'border-base-100 h-[168px] w-full rounded-[12px] border p-[12px] font-mono text-[13px]'
                        }
                        data-testid={'compensation-import-textarea'}
                    />
                </div>

                {result === null ? null : (
                    <div
                        className={'bg-base-25 flex flex-col gap-1 rounded-[12px] p-[12px]'}
                        data-testid={'compensation-import-review'}
                    >
                        <Text
                            weight={'semibold'}
                            color={Colors.NAVY}
                        >
                            {t('compensation.import.matched', { count: recognizedCount })}
                        </Text>

                        {recognizedCount > 0 ? (
                            <Text color={Colors.BASE_500}>
                                {matchedOverflowCount > 0
                                    ? `${matchedPreview} ${t('compensation.import.matchedMore', {
                                          count: matchedOverflowCount,
                                      })}`
                                    : matchedPreview}
                            </Text>
                        ) : null}

                        {result.unrecognized.length > 0 ? (
                            <Text color={Colors.BASE_500}>
                                {t('compensation.import.unmatched', {
                                    count: result.unrecognized.length,
                                    names: result.unrecognized
                                        .slice(0, REVIEW_PREVIEW_LIMIT)
                                        .map((row) => row.rawName || row.rawEmail || '')
                                        .join(', '),
                                })}
                            </Text>
                        ) : null}

                        {result.duplicates.length > 0 ? (
                            <Text color={Colors.BASE_500}>
                                {t('compensation.import.duplicate', {
                                    count: result.duplicates.length,
                                    names: result.duplicates
                                        .slice(0, REVIEW_PREVIEW_LIMIT)
                                        .map((duplicate) => duplicate.displayName)
                                        .join(', '),
                                })}
                            </Text>
                        ) : null}

                        {result.invalid.map((row) => (
                            <Text
                                key={row.line}
                                color={Colors.BASE_500}
                            >
                                {`${t('compensation.import.invalidRow', { line: row.line })} ${t(
                                    ISSUE_KEYS[row.reason],
                                )}`}
                            </Text>
                        ))}
                    </div>
                )}
            </div>
        </ModalDialog>
    );
};
